import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, type SxProps, type Theme, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { forwardRef, type KeyboardEvent } from "react";
import { COVERAGE, GRID_FONT, GRID_RADIUS, INK, SURFACE } from "../../../theme/tokens";

/**
 * Fill-% at/above which a section reads as a calm amber "mostly filled" heads-up
 * rather than critical red. Below it the section is materially under-filled and
 * grades red; at/above 100% it grades positive. Widened from a 90% cutoff so a
 * near-full section (e.g. 89%) no longer reads alarmingly red.
 */
const FILL_WARNING_THRESHOLD_PCT = 75;

type Props = {
	/** Section title (already translated / domain string). */
	name: string;
	/**
	 * Cycle fill percentage over required demand, or `null` when the section has
	 * NO configured targets (nothing to fill) — rendered as the `noTargetsLabel`
	 * instead of a misleading 0/100%.
	 */
	fillPct: number | null;
	/** Count of cells below their safety minimum in this section. */
	gapCount: number;
	isCollapsed: boolean;
	onToggle: () => void;
	/** CSS grid-column for the full-width band (e.g. `"1 / span 24"`). */
	gridColumn: string;
	/** Already-translated label for the no-targets state. */
	noTargetsLabel: string;
	/** Already-translated fill label, e.g. "82% filled". */
	fillLabel: string;
	/** Already-translated gaps label, e.g. "3 gaps". */
	gapsLabel: string;
	/** Already-translated aria-label for the toggle button. */
	toggleAriaLabel: string;
	/**
	 * Optional "add to this group" action. When provided, a small, subtle "+" icon
	 * button appears in the band (hover-revealed on hover-capable devices, always
	 * shown on touch) — e.g. "add a location/room to this section". Click does not
	 * toggle the section. Keep the label already-translated.
	 */
	onAdd?: () => void;
	/** Already-translated aria-label for the add button (required when `onAdd` is set). */
	addLabel?: string;
	/** Test id for the add button. */
	addDataTestId?: string;
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * Section roll-up band for the coverage grids: a sticky, full-width header that
 * names a group of staffing rows and surfaces its two roll-up signals — cycle
 * fill % and the count of below-minimum gaps — as graded chips. A brand
 * left-accent rule and a tinted gradient read it as a structural band, distinct
 * from the data rows under it; clicking toggles the section.
 *
 * Generic over any grouped supply-vs-demand grid — it owns only the band's
 * presentation + the fill/gap chip color grading, never page data (the caller
 * computes the roll-up and passes already-translated labels). Shared by the
 * Summary and Detail coverage grids so the band can never drift between them.
 */
export const CoverageSectionHeader = forwardRef<HTMLDivElement, Props>(
	(
		{
			name,
			fillPct,
			gapCount,
			isCollapsed,
			onToggle,
			gridColumn,
			noTargetsLabel,
			fillLabel,
			gapsLabel,
			toggleAriaLabel,
			onAdd,
			addLabel,
			addDataTestId,
			dataTestId,
			sx,
		},
		ref,
	) => {
		// Fill grades: fully met (>=100%) reads positive; "mostly filled" (>=75%) reads
		// as a calm amber heads-up — an 89%-filled section is NOT an alarm; only a
		// materially under-filled section (<75%) reads critical red. No-targets → muted.
		const fillColor =
			fillPct === null
				? INK.MUTED
				: fillPct >= 100
					? COVERAGE.SURPLUS_INK
					: fillPct >= FILL_WARNING_THRESHOLD_PCT
						? COVERAGE.WARNING_INK
						: COVERAGE.CRITICAL_INK;
		const hasGaps = fillPct !== null && gapCount > 0;

		// The band is a `role="button"` div (not a native `<button>`) so the optional
		// add affordance can be a real nested `<button>` — nesting a button inside a
		// native button is invalid HTML.
		const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				onToggle();
			}
		};

		return (
			<Box
				ref={ref}
				role="button"
				tabIndex={0}
				aria-expanded={!isCollapsed}
				aria-label={toggleAriaLabel}
				onClick={onToggle}
				onKeyDown={handleKeyDown}
				data-testid={dataTestId}
				sx={[
					(theme) => ({
						gridColumn,
						display: "block",
						p: 0,
						width: "100%",
						border: "none",
						// Brand left-accent rule makes the band read as a labeled divider.
						borderLeft: `3px solid ${theme.palette.primary.main}`,
						font: "inherit",
						textAlign: "left",
						cursor: "pointer",
						outline: "none",
						backgroundColor: SURFACE,
						backgroundImage: `linear-gradient(${alpha(theme.palette.primary.light, 0.2)}, ${alpha(theme.palette.primary.light, 0.2)})`,
						color: theme.palette.primary.dark,
						transition: "background-color 150ms cubic-bezier(0.23, 1, 0.32, 1)",
						// The add ("+") button is always visible so it's discoverable (not hover-gated).
						"@media (hover: hover) and (pointer: fine)": {
							"&:hover": { backgroundImage: `linear-gradient(${alpha(theme.palette.primary.light, 0.32)}, ${alpha(theme.palette.primary.light, 0.32)})` },
						},
						"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px" },
					}),
					...(Array.isArray(sx) ? sx : [sx]),
				]}
			>
				{/* The band spans every day column (full grid width), so it can't itself stick —
			    a full-width element has no room to slide. The label cluster (name + roll-up
			    chips) instead lives in its OWN sticky-left region pinned to the grid's left
			    edge, so the section identity + its signals stay in view at any horizontal
			    scroll position (they used to be pushed off-screen via `ml:auto`). */}
				<Box
					sx={{
						position: "sticky",
						left: 0,
						width: "fit-content",
						maxWidth: "100%",
						display: "flex",
						alignItems: "center",
						gap: 1,
						pl: 1.25,
						pr: 1.5,
						py: 0.75,
					}}
				>
					{isCollapsed ? <KeyboardArrowRight sx={{ fontSize: 18, flexShrink: 0 }} /> : <KeyboardArrowDown sx={{ fontSize: 18, flexShrink: 0 }} />}
					<Box
						component="span"
						sx={{
							minWidth: 0,
							fontWeight: 800,
							fontSize: GRID_FONT.MD,
							letterSpacing: GRID_FONT.LETTER_SPACING.TIGHT,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{name}
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
						<Typography
							component="span"
							sx={{
								fontSize: GRID_FONT.SM,
								fontWeight: 700,
								color: fillColor,
								backgroundColor: alpha(fillColor, 0.12),
								px: 0.75,
								py: "1px",
								borderRadius: GRID_RADIUS.PANEL,
								whiteSpace: "nowrap",
							}}
						>
							{fillPct === null ? noTargetsLabel : fillLabel}
						</Typography>
						{hasGaps && (
							<Typography
								component="span"
								sx={{
									fontSize: GRID_FONT.SM,
									fontWeight: 800,
									color: COVERAGE.CRITICAL_INK,
									backgroundColor: COVERAGE.CRITICAL_BG,
									px: 0.75,
									py: "1px",
									borderRadius: GRID_RADIUS.PANEL,
									display: "flex",
									alignItems: "center",
									gap: 0.25,
									whiteSpace: "nowrap",
								}}
							>
								<WarningAmberIcon sx={{ fontSize: 13 }} />
								{gapsLabel}
							</Typography>
						)}
					</Box>
					{onAdd && (
						<Box
							component="button"
							type="button"
							className="ms3-section-add-btn"
							aria-label={addLabel}
							data-testid={addDataTestId}
							onClick={(event) => {
								event.stopPropagation();
								onAdd();
							}}
							sx={(theme) => ({
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
								width: 22,
								height: 22,
								p: 0,
								border: "none",
								borderRadius: GRID_RADIUS.PANEL,
								cursor: "pointer",
								color: theme.palette.primary.main,
								backgroundColor: alpha(theme.palette.primary.main, 0.1),
								transition: "opacity 150ms cubic-bezier(0.23, 1, 0.32, 1), background-color 150ms",
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.2) },
								},
								"&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "1px" },
							})}
						>
							<AddIcon sx={{ fontSize: 15 }} />
						</Box>
					)}
				</Box>
			</Box>
		);
	},
);

CoverageSectionHeader.displayName = "CoverageSectionHeader";
